/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChronoUnit, Instant, LocalDateTime } from 'js-joda';
import { createSelector, defaultMemoize } from 'reselect';
import { oc } from 'ts-optchain';
import Fuse from 'fuse.js';
import _ from 'lodash';

import { DisplayTime } from '@tock/time';
import { Party, PartyList, ReservationsMetaSection, WalkInsMetaSection } from 'types/service';
import { PartyListMetaSections } from 'constants/service/partyListTabs';
import { PartyListSections } from 'constants/service/singlePartyList';
import {
  SelectedExperience,
  cappedSelectedPartyListSizesSelector,
  selectedPartyListExperiencesSelector,
  selectedPartyListFilterAmountDueSelector,
  selectedPartyListFilterUnansweredSelector,
  selectedPartyListFilterUnfiredItemsSelector,
  selectedPartyListGuestTagsSelector,
  selectedPartyListSeatingOptionsSelector,
  selectedPartyListServicesSelector,
  selectedPartyListStatusSelector,
  selectedPartyListSupplementsSelector,
  selectedPartyListVisitTagsSelector,
} from 'selectors/calendarFilterSelectors';
import { SelectedPartyListTabs } from 'state/service/ui';
import { StoreType } from '@tock/shared/utils/connect';
import {
  checkHasUnreadTextMessage,
  getPartyTime,
  isPartyArrived,
  isPartyCanceled,
  isPartyExpected,
  isPartyNoShow,
  isPartyPartiallyArrived,
  isPartyQuotedOriginalWalkIn,
} from 'utils/partyUtils';
import { core } from '@tock/coretypes';
import { fastParseDateTime } from 'utils/timeUtils';
import { featuresSelector, flexSelector } from 'selectors/featuresSelector';
import {
  filterByAmountDue,
  filterByGuestTags,
  filterByQuestionsUnanswered,
  filterBySelectedGuestListTab,
  filterBySelectedPartyListExperiences,
  filterBySelectedPartyListSeatingOptions,
  filterBySelectedPartyListServices,
  filterBySelectedPartyListSizes,
  filterBySelectedPartyListStatus,
  filterBySupplements,
  filterByUnfiredItems,
  filterByVisitTagsParty,
  getPartyFilterValues,
} from 'utils/service/partyListUtils';
import { getPartyListTabForParty } from 'utils/service/partyListTabsUtils';
import { getPatronKeyContactAttributes } from 'components/service/party/partyRedesignCard/partyType';
import {
  selectedGuestListTabSelector,
  selectedPartyListTabsSelector,
  walkInSortSelector,
} from 'selectors/service/serviceUiSelector';
import { twelveHourFormatter } from '@tock/i18n';
import currentBusinessSelector from 'selectors/service/currentBusinessSelector';
import partiesSelector from 'selectors/service/partiesSelector';

export interface Section {
  dateTime: LocalDateTime;
  key: string;
  label: string;
  numCovers: number;
  parties: Party[];
  time: string;
}
interface PartiesByNoShowStatus {
  cancellations: Section[];
  show: Section[];
  noShow: Section[];
}

type PartyListMeta = ReservationsMetaSection | WalkInsMetaSection;

const PARTY_SECTION_LABELS: { [key: string]: string } = {
  walkins: 'Waitlist (Walk-ins)',
  upcoming: 'Upcoming',
  cancellations: 'Cancellations',
  noShows: 'No-shows',
  finished: 'Finished',
  seated: 'Seated',
};

const walkInsGroupKey = 'Waitlist (Walk-ins)';

const sortWalkInsByArrival = (parties: Party[]) => {
  return _.orderBy(parties, [
    (party) => oc(party).arrivalTime(0),
    (party) => oc(party).walkInCreatedTimeSec(0),
  ]);
};

const sortPartiesByExpectedTime = (parties: Party[]): Party[] => {
  return _.orderBy(
    parties,
    (party) => oc(party).arrivalTime(0) + oc(party).quotedTimeMinutes(0) * 60
  );
};

const sortWalkIns = (parties: Party[], walkInSortByArrival: boolean) => {
  if (walkInSortByArrival) {
    return sortWalkInsByArrival(parties);
  } else {
    return sortPartiesByExpectedTime(parties);
  }
};

const filterBySelectedPartyListTabs = (selectedPartyListTabs: any, party: Party): boolean => {
  return selectedPartyListTabs[getPartyListTabForParty(party)];
};

const getWalkinGroupKeyOrPurchasePartyTimeString = (
  timeZone: string,
  party: Party,
  showDeliveryOrderAtPickupTime = false,
  shouldSortReservationsBySeatedTime = false
): string => {
  if (isPartyQuotedOriginalWalkIn(party) && isPartyExpected(party)) {
    return walkInsGroupKey;
  } else {
    return getPartyTime(
      party,
      timeZone,
      showDeliveryOrderAtPickupTime,
      shouldSortReservationsBySeatedTime
    ).toString();
  }
};

const sortPartiesByNameAndStatus = (parties: Party[], byFirstName: boolean): Party[] => {
  const arrivedParties = _.sortBy(
    _.filter(parties, (party) => isPartyArrived(party) && !!party.arrivalTime),
    (party) => oc(party).arrivalTime()
  );
  const partiallyArrivedParties = _.sortBy(
    _.filter(parties, (party) => isPartyPartiallyArrived(party) && !!party.arrivalTime),
    (party) => oc(party).arrivalTime()
  );
  const expectedParties = _.filter(
    parties,
    (party) => !isPartyArrived(party) && !isPartyPartiallyArrived(party)
  );
  const sort = (party: Party) => {
    const keyContactAttributes = getPatronKeyContactAttributes(party);
    const firstName = keyContactAttributes.firstName ?? party.name;
    const lastNameFirstName = keyContactAttributes.lastNameFirstName ?? party.lastNameFirstName;
    return _.lowerCase(byFirstName ? firstName : lastNameFirstName);
  };
  return _.concat(arrivedParties, partiallyArrivedParties, _.sortBy(expectedParties, sort));
};

const makePartyListGroup = (
  walkInSortByArrival: boolean,
  byFirstName: boolean,
  [key, value]: [string, Party[]]
) => {
  const partyListGroup = {
    key,
    numCovers: _.sumBy(value, 'partySize') || 0,
    numParties: value.length,
  };
  if (key === walkInsGroupKey) {
    const sortedPartiesByExpectedTime = sortWalkIns(value, walkInSortByArrival);
    return _.assign(partyListGroup, {
      label: key,
      parties: sortedPartiesByExpectedTime,
    });
  } else {
    const dateTime = fastParseDateTime(key);
    const label = <DisplayTime timeValue={dateTime} />;
    return _.assign(partyListGroup, {
      label,
      dateTime,
      time: twelveHourFormatter.format(dateTime.toLocalTime()),
      parties: sortPartiesByNameAndStatus(value, byFirstName),
    });
  }
};

const sortPartyListGroup = (group: any) => {
  if (group.key === walkInsGroupKey) {
    //Force walk-ins to be first
    return '0000';
  } else {
    return group.dateTime.toString();
  }
};

const getQuotedTime = (party?: Party) => {
  if (!party) {
    return undefined;
  }

  if (party.arrivalTime && party.quotedTimeMinutes) {
    const quotedTimeInstant = Instant.ofEpochSecond(party.arrivalTime, 0).plus(
      party.quotedTimeMinutes,
      ChronoUnit.MINUTES
    );
    return LocalDateTime.ofInstant(quotedTimeInstant);
  }

  if (party.expectedReservationTime) {
    return fastParseDateTime(party.expectedReservationTime);
  }

  return undefined;
};

/**
 * Create summary objects of categories of parties.  Categories are based off the tabs on the guest
 * list or categories on a single guest list.
 */
export const formatPartyListSection = (
  section: any,
  sectionType: string,
  numParties?: number
): PartyListMeta => {
  return sectionType === 'walkins'
    ? {
        isWalkIns: true,
        hasUnreadTextMessage: _.some(section.parties, (party: Party) =>
          checkHasUnreadTextMessage(party.textMessage)
        ),
        label: PARTY_SECTION_LABELS[sectionType],
        type: sectionType as PartyListSections,
        nextPartyExpectedTime: getQuotedTime(_.head(section.parties)),
        numCovers: section.numCovers || 0,
        numParties,
        section,
      }
    : {
        isWalkIns: false,
        label: PARTY_SECTION_LABELS[sectionType],
        numCovers: _.sumBy(section, 'numCovers'),
        numParties,
        sections: section,
        type: sectionType as PartyListSections,
      };
};

const separateWalkIns = (sections: any): PartyList => {
  const separatedWalkIns = _.groupBy(sections, (section: any) => {
    if (section.label === walkInsGroupKey) {
      return PartyListMetaSections.walkins;
    } else {
      return PartyListMetaSections.upcoming;
    }
  });

  // There can only be one walk-in section
  const walkInSection: any = _.head(separatedWalkIns[PartyListMetaSections.walkins]) || {};

  const reservationSectionsIncludingNoShows =
    separatedWalkIns[PartyListMetaSections.upcoming] || [];
  const separatedNoShows = extractNoShows(reservationSectionsIncludingNoShows);

  const reservationsSections = separatedNoShows.show;
  const noShowSections = separatedNoShows.noShow;
  const cancellationsSections = separatedNoShows.cancellations;

  return {
    [PartyListMetaSections.walkins]: formatPartyListSection(
      walkInSection,
      PartyListMetaSections.walkins
    ) as WalkInsMetaSection,
    [PartyListMetaSections.upcoming]: formatPartyListSection(
      reservationsSections,
      PartyListMetaSections.upcoming
    ) as ReservationsMetaSection,
    [PartyListMetaSections.noShows]: formatPartyListSection(
      noShowSections,
      PartyListMetaSections.noShows
    ) as ReservationsMetaSection,
    [PartyListMetaSections.cancellations]: formatPartyListSection(
      cancellationsSections,
      PartyListMetaSections.cancellations
    ) as ReservationsMetaSection,
  };
};

interface SelectedPartyListFilters {
  selectedGuestListTab: 'guestList' | 'notifyList' | 'orderList';
  selectedPartyListTabs: SelectedPartyListTabs;
  selectedPartyListServices: number[];
  selectedPartyListExperiences: SelectedExperience[];
  selectedPartyListSeatingOptions: string[];
  selectedPartyListSizes: string[];
  selectedPartyListStatus: string[];
  selectedPartyListGuestTags: string[];
  selectedPartyListVisitTags: string[];
  selectedPartyListSupplements: number[];
  selectedPartyListFilterUnanswered: { showUnansweredQuestions: boolean };
  selectedPartyListFilterUnfiredItems: { showUnfiredItems: boolean };
  selectedPartyListFilterAmountDue: { showAmountDue: boolean };
}

export const getPartyListTextFilterSelector = (state: StoreType) =>
  state.service.ui.partyListTextFilter;

const filterPartyOn: Fuse.IFuseOptions<Party> = {
  keys: ['name', 'patronInfo.email', 'phone'],
  threshold: 0.4,
};

const getPartyQueryFilterer = defaultMemoize((parties: Party[]) => {
  return new Fuse(parties, filterPartyOn);
});

/**
 * Inclusive filters that are applied to an array of parties
 */
export const filterParties = (
  parties: Party[],
  filters: SelectedPartyListFilters,
  query: string
) => {
  if (!parties.length) {
    return [];
  }

  const {
    selectedGuestListTab,
    selectedPartyListServices,
    selectedPartyListExperiences,
    selectedPartyListSeatingOptions,
    selectedPartyListSizes,
    selectedPartyListStatus,
    selectedPartyListGuestTags,
    selectedPartyListVisitTags,
    selectedPartyListSupplements,
    selectedPartyListFilterUnanswered,
    selectedPartyListFilterAmountDue,
    selectedPartyListFilterUnfiredItems,
  } = filters;

  const queryFilter = getPartyQueryFilterer(parties);

  const queriesParties = !_.isEmpty(query)
    ? (_.map(queryFilter.search(query), (result) => result.item) as Party[])
    : parties;

  return queriesParties.filter((party) => {
    const {
      experienceId,
      experienceName,
      variety,
      guestProfile,
      partySize,
      serviceType,
      seatingOptionEnumValue,
    } = getPartyFilterValues(party);

    return (
      filterBySelectedGuestListTab(selectedGuestListTab, variety) &&
      filterBySelectedPartyListExperiences(selectedPartyListExperiences, {
        experienceName,
        experienceId,
      }) &&
      filterBySelectedPartyListSeatingOptions(
        selectedPartyListSeatingOptions,
        seatingOptionEnumValue
      ) &&
      filterBySelectedPartyListServices(selectedPartyListServices, serviceType) &&
      filterBySelectedPartyListSizes(selectedPartyListSizes, partySize) &&
      filterBySelectedPartyListStatus(selectedPartyListStatus, party) &&
      filterByVisitTagsParty(selectedPartyListVisitTags, party) &&
      filterByGuestTags(selectedPartyListGuestTags, guestProfile) &&
      filterBySupplements(selectedPartyListSupplements, party) &&
      filterByQuestionsUnanswered(selectedPartyListFilterUnanswered, party) &&
      filterByUnfiredItems(selectedPartyListFilterUnfiredItems, party) &&
      filterByAmountDue(selectedPartyListFilterAmountDue, party)
    );
  });
};

/**
 * Sorts an array of parties in to sections based on the time of the reservation
 */
export const sortPartiesByTime = (
  parties: Party[],
  timeZone: string,
  walkInSortByArrival: boolean,
  byFirstName: boolean,
  showDeliveryOrderAtPickupTime = false,
  shouldSortReservationsBySeatedTime = false
) => {
  const groupedParties = _.groupBy(parties, (party: Party) =>
    getWalkinGroupKeyOrPurchasePartyTimeString(
      timeZone,
      party,
      showDeliveryOrderAtPickupTime,
      shouldSortReservationsBySeatedTime
    )
  );
  const pairedParties = _.toPairs(groupedParties);
  const partyListGroupedParties = _.map(pairedParties, (partyPair) =>
    makePartyListGroup(walkInSortByArrival, byFirstName, partyPair)
  );
  const sortedGroups = _.sortBy(partyListGroupedParties, sortPartyListGroup);
  return _.toArray(sortedGroups);
};

/**
 * Returns a section at the same time as the given section but with no covers.
 */
function copySectionWithBlankCovers(section: Section): Section {
  return {
    ...section,
    numCovers: 0,
    parties: [],
  };
}

/**
 * Takes a list of sections where parties include both no-shows and "shows", and returns
 * two parallel lists of sections, one containing only no-shows and one containing only shows.
 */
function extractNoShows(sections: Section[]): PartiesByNoShowStatus {
  const show: Section[] = [];
  const noShow: Section[] = [];
  const cancellations: Section[] = [];
  _.forEach(sections, (section) => {
    const thisSectionShow: Section = copySectionWithBlankCovers(section);
    const thisSectionNoShow: Section = copySectionWithBlankCovers(section);
    const thisSectionCancellations: Section = copySectionWithBlankCovers(section);
    _.forEach(section.parties, (party) => {
      let sectionToUpdate;
      if (isPartyNoShow(party)) {
        sectionToUpdate = thisSectionNoShow;
      } else if (isPartyCanceled(party)) {
        sectionToUpdate = thisSectionCancellations;
      } else {
        sectionToUpdate = thisSectionShow;
      }
      sectionToUpdate.parties.push(party);
      sectionToUpdate.numCovers += party.partySize;
    });
    if (thisSectionShow.numCovers > 0) {
      show.push(thisSectionShow);
    }
    if (thisSectionNoShow.numCovers > 0) {
      noShow.push(thisSectionNoShow);
    }
    if (thisSectionCancellations.numCovers > 0) {
      cancellations.push(thisSectionCancellations);
    }
  });
  return {
    cancellations,
    show,
    noShow,
  };
}

// this is to get around TS defs for reselect, and maybe this grouping makes sense anyway?
export const selectedPartyListFilterValuesSelector = createSelector(
  selectedGuestListTabSelector,
  selectedPartyListTabsSelector,
  selectedPartyListServicesSelector,
  selectedPartyListSeatingOptionsSelector,
  selectedPartyListExperiencesSelector,
  cappedSelectedPartyListSizesSelector,
  selectedPartyListStatusSelector,
  selectedPartyListGuestTagsSelector,
  selectedPartyListVisitTagsSelector,
  selectedPartyListSupplementsSelector,
  selectedPartyListFilterUnansweredSelector,
  selectedPartyListFilterUnfiredItemsSelector,
  selectedPartyListFilterAmountDueSelector,
  (
    selectedGuestListTab,
    selectedPartyListTabs,
    selectedPartyListServices,
    selectedPartyListSeatingOptions,
    selectedPartyListExperiences,
    selectedPartyListSizes,
    selectedPartyListStatus,
    selectedPartyListGuestTags,
    selectedPartyListVisitTags,
    selectedPartyListSupplements,
    selectedPartyListFilterUnanswered,
    selectedPartyListFilterUnfiredItems,
    selectedPartyListFilterAmountDue
  ) => ({
    selectedGuestListTab,
    selectedPartyListTabs,
    selectedPartyListServices,
    selectedPartyListSeatingOptions,
    selectedPartyListExperiences,
    selectedPartyListSizes,
    selectedPartyListStatus,
    selectedPartyListGuestTags,
    selectedPartyListVisitTags,
    selectedPartyListSupplements,
    selectedPartyListFilterUnanswered,
    selectedPartyListFilterUnfiredItems,
    selectedPartyListFilterAmountDue,
  })
);

export const largePartiesSelector = createSelector(partiesSelector, (parties): Party[] => {
  const largeParties = parties.filter(
    (party) => party.isLargeParty && !isPartyCanceled(party) && !isPartyNoShow(party)
  );
  return _.orderBy(largeParties, (party) => [party.expectedReservationTime, party.partySize]);
});

export default createSelector(
  partiesSelector,
  flexSelector,
  getPartyListTextFilterSelector,
  selectedPartyListFilterValuesSelector,
  walkInSortSelector,
  currentBusinessSelector,
  featuresSelector,
  (
    parties,
    isFlex,
    textFilter,
    selectedPartyListFilters,
    walkInSortByArrival,
    currentBusiness,
    features
  ): PartyList => {
    const filteredParties = filterParties(parties, selectedPartyListFilters, textFilter).filter(
      (party: Party) => {
        const { selectedPartyListTabs } = selectedPartyListFilters;

        return filterBySelectedPartyListTabs(selectedPartyListTabs, party);
      }
    );

    const timeZone = currentBusiness?.timeZone ?? 'US/Central';

    const featureForFirstNameSorting: core.Feature.FeatureName = isFlex
      ? 'PARTY_CARD_REDESIGN'
      : 'PARTY_CARD_REDESIGN_SLOT';

    const groupsList = sortPartiesByTime(
      filteredParties,
      timeZone,
      walkInSortByArrival,
      features[featureForFirstNameSorting] ?? false,
      features.SHOW_DELIVERY_ORDER_AT_PICKUP_TIME,
      features.USE_ADJUSTED_VISIT_TIMES
    );

    return separateWalkIns(groupsList);
  }
);
